import React, { useState, useEffect } from 'react';
import {
  NetworkSection,
  IdentitySection,
  ObjectiveSection,
  ExtraSection,
  IdealConnectionSection,
} from '../../components/onboarding/summary';
import { Button, Spinner, Toaster } from '../../components/common';
import type { OnboardingSummary } from '../../types/onboarding';
import {
  getOnboardingSummary,
  requestAISummary,
  resetOnboarding,
} from '../../services/onboardingService';
import RestartIcon from '../../assets/images/restart.svg';
import { MdArrowForward } from 'react-icons/md';
import { routes } from '../../lib/routes';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { groupQuestionsBySection } from '../../utils';

const OnboardingSummaryPage = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingAISummary, setIsGeneratingAISummary] = useState(false);
  const [summary, setSummary] = useState<OnboardingSummary[]>([]);

  useEffect(() => {
    setIsLoading(true);
    getOnboardingSummary()
      .then(res => {
        const summary = groupQuestionsBySection(res.result);
        const formattedSummary = summary.map(section => ({
          ...section,
          questions: section.questions.map(question => ({
            ...question,
            user_response:
              question.code === 'resume_linkedin_bio'
                ? JSON.parse(question.user_response as string)
                : (question.user_response as string),
          })),
        }));
        setSummary(formattedSummary);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleStartOver = () => {
    resetOnboarding().then(res => {
      if (res.code !== 200) {
        toast.custom(t => (
          <Toaster message={res.message} type='error' id={t.id} />
        ));
        return;
      }
      window.location.href = routes.onboardingChat;
    });
  };

  const handleGenerateAISummary = () => {
    setIsGeneratingAISummary(true);
    requestAISummary()
      .then(res => {
        if (res.code !== 200) {
          toast.custom(t => (
            <Toaster message={res.message} type='error' id={t.id} />
          ));
        }

        navigate(routes.onboardingAISummary);
      })
      .finally(() => {
        setIsGeneratingAISummary(false);
      });
  };

  return (
    <div className='container-xl px-4 py-6 !mt-2 md:px-8 md:py-[1.375rem] h-[calc(100vh-6rem)] overflow-y-auto'>
      {isLoading ? (
        <div className='flex justify-center items-center h-full w-full'>
          <Spinner size='xl' />
        </div>
      ) : (
        <>
          <div className='mb-8'>
            <h2 className='mb-1 font-heading !font-medium text-heading-500'>
              Onboarding Summary
            </h2>
            <p className='mb-0 text-body-400'>
              🎉 Almost there...! Here's your onboarding questionnaire summary
            </p>
          </div>

          {summary.map(section => (
            <div
              key={section.section_id}
              className='sm:p-8 p-4 mb-2 bg-gray-light-100 rounded-2xl'
            >
              {section.section_code === 'identity' && (
                <IdentitySection section={section} />
              )}
              {section.section_code === 'core_objectives' && (
                <ObjectiveSection section={section} />
              )}
              {section.section_code === 'ideal_connections' && (
                <NetworkSection section={section} />
              )}
              {section.section_code === 'style_prefs' && (
                <IdealConnectionSection section={section} />
              )}
              {section.section_code === 'extra' && (
                <ExtraSection section={section} />
              )}
            </div>
          ))}

          <div className='sm:flex justify-end gap-2'>
            <Button
              className='flex items-center gap-2 sm:mb-0 mb-2 sm:w-fit w-full'
              variant='outline'
              onClick={handleStartOver}
            >
              <img
                className='w-6 h-6 object-contain'
                src={RestartIcon}
                alt='Start Over'
              />
              Start Over
            </Button>
            <Button
              className='flex items-center gap-2 sm:w-fit w-full'
              color='dark'
              onClick={handleGenerateAISummary}
              isLoading={isGeneratingAISummary}
              disabled={isGeneratingAISummary}
            >
              <MdArrowForward size={24} />
              Generate AI Summary
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default OnboardingSummaryPage;
